/**
 * はじめに（/welcome。#152、docs/61 ★e・§2 ⑥、Figma「アカウントと氏名（10/8）」⑥。#142 段4 の業種の1枚＝/welcome/industry を広げた）
 *   ログインの枠の形（ロゴ・二重罫）。題「はじめに」、足りないものだけ聞く：
 *     ・姓・名（人の氏名がそろっていない＝Me.account.needsName。Google・Apple から届いた分は入れておく）
 *     ・業種（業種の無い会社で、まだ会社名も無い＝始めたばかりの会社・管理者だけ。分類と品目の見本が業種で入る＝PUT /company/industry seed=true）
 *   ボタン「利用開始」→ ホーム。氏名がそろっていない人は Shell の画面を開くとここへ来る（飛ばせない。app.tsx の NeedsName）。
 *   足りないものが無ければホームへ
 */
import { ACCOUNT_NAME_MSG, DEFAULT_INDUSTRY, PersonName, type Industry, type Me } from "@genbaryoku/contract";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router";
import { api, ApiError, messageOf } from "../api.js";
import { ME_KEY, useMe } from "../auth.js";
import { IndustrySelect } from "../components/IndustrySelect.jsx";
import { AuthCard, ErrorText, Field, guardComposingEnter } from "../components/ui.jsx";

/** 業種も聞くか：業種の無い会社で、まだ会社名も無い（始めたばかり）・管理者（業種を入れられるのは管理者だけ） */
export const needsIndustry = (me: Me): boolean => me.tenant.industry === null && me.tenant.needsCompanyName && me.user.role === "admin";

export function WelcomePage() {
  const me = useMe();
  const nav = useNavigate();
  const qc = useQueryClient();
  const product = me.data?.tenant.product ?? "genba";
  const [family, setFamily] = useState("");
  const [given, setGiven] = useState("");
  const [industry, setIndustry] = useState<Industry>(DEFAULT_INDUSTRY[product]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [fieldErr, setFieldErr] = useState<{ familyName?: string; givenName?: string }>({});
  // 届いた分（Google・Apple の片方など）を入れておく
  const acc = me.data?.account;
  useEffect(() => {
    if (!acc) return;
    setFamily(acc.familyName);
    setGiven(acc.givenName);
  }, [acc?.familyName, acc?.givenName]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setIndustry(DEFAULT_INDUSTRY[product]);
  }, [product]);

  if (!me.data) return null; // RequireAuth が先に見ている
  const askName = me.data.account.needsName;
  const askIndustry = needsIndustry(me.data);
  if (!askName && !askIndustry) return <Navigate to="/home" replace />;

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(undefined);
    setFieldErr({});
    const name = askName ? PersonName.safeParse({ familyName: family, givenName: given }) : null;
    if (name && !name.success) {
      const errs: { familyName?: string; givenName?: string } = {};
      for (const i of name.error.issues) {
        const k = i.path[0] === "givenName" ? "givenName" : "familyName";
        errs[k] ??= i.message;
      }
      setFieldErr(errs);
      document.getElementById(errs.familyName ? "welcome-family" : "welcome-given")?.focus();
      return;
    }
    setBusy(true);
    try {
      if (name?.success) await api("PUT", "/account/profile", name.data);
      if (askIndustry) await api("PUT", "/company/industry", { industry, seed: true });
      await qc.invalidateQueries({ queryKey: ME_KEY });
      nav("/home", { replace: true, state: { justRegistered: true } });
    } catch (x) {
      if (x instanceof ApiError && x.problem.errors) setFieldErr(x.problem.errors as { familyName?: string; givenName?: string });
      setError(messageOf(x));
      setBusy(false);
    }
  };
  return (
    <AuthCard title={ACCOUNT_NAME_MSG.welcomeTitle} onSubmit={submit} plain>
      {askName ? (
        <>
          <div className="namerow" data-testid="welcome-name">
            <Field id="welcome-family" label={ACCOUNT_NAME_MSG.family}>
              <input id="welcome-family" className="inp" autoComplete="family-name" maxLength={50} value={family} aria-invalid={fieldErr.familyName ? true : undefined} onKeyDown={guardComposingEnter} onChange={(e) => setFamily(e.target.value)} />
            </Field>
            <Field id="welcome-given" label={ACCOUNT_NAME_MSG.given}>
              <input id="welcome-given" className="inp" autoComplete="given-name" maxLength={50} value={given} aria-invalid={fieldErr.givenName ? true : undefined} onKeyDown={guardComposingEnter} onChange={(e) => setGiven(e.target.value)} />
            </Field>
          </div>
          <p className="hint" style={{ margin: 0 }}>
            {ACCOUNT_NAME_MSG.sameEverywhere}
          </p>
        </>
      ) : null}
      {askIndustry ? <IndustrySelect id="welcome-industry" product={product} value={industry} onChange={setIndustry} /> : null}
      <ErrorText>{fieldErr.familyName ?? fieldErr.givenName ?? error}</ErrorText>
      <button type="submit" className="btn primary big" disabled={busy} data-testid="welcome-submit">
        {ACCOUNT_NAME_MSG.welcomeStart}
      </button>
    </AuthCard>
  );
}
